import React, { useEffect, useState } from 'react';
import { Platform, View, Text, TextInput, TouchableOpacity, StyleSheet, Pressable, ScrollView, KeyboardAvoidingView } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { createReview, fetchTemplates, fetchReviews } from '../store/slices/reviewSlice';
import { reviewService } from '../services/reviewService';
import { api } from '../services/api';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { useSync } from '../hooks/useSync';
import { getOfflineReviewService } from '../services/platformServices';

const offlineReviewService = getOfflineReviewService();

type HomeScreenProps = {
  activeReview?: any;
  setActiveReview?: React.Dispatch<React.SetStateAction<any>>;
  route?: any;
};

const HomeScreen: React.FC<HomeScreenProps> = ({ activeReview: propActiveReview, setActiveReview: propSetActiveReview, route }) => {

  // Use local state if props aren't provided (when used in navigation)
  const [localActiveReview, setLocalActiveReview] = useState(null);
  const activeReview = propActiveReview !== undefined ? propActiveReview : localActiveReview;
  const setActiveReview = propSetActiveReview || setLocalActiveReview;

  // Network status for offline-aware address autocomplete
  const { isOnline } = useSync();
  const useOnlineAddressLookup = isOnline;

  const [showNewReview, setShowNewReview] = React.useState(false);
  const [address, setAddress] = React.useState('');
  const [memberName, setMemberName] = React.useState('');
  const [memberEmail, setMemberEmail] = React.useState('');
  const [homeManagerName, setHomeManagerName] = React.useState('');
  const [homeManagerEmail, setHomeManagerEmail] = React.useState('');
  const [addressDetails, setAddressDetails] = React.useState(null);
  const [cachedAddresses, setCachedAddresses] = React.useState<Array<{
    propertyId: number;
    address: string;
    lastUsed: Date;
  }>>([]);
  const [selectedPropertyId, setSelectedPropertyId] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [isCreating, setIsCreating] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);
  const [templatesLoaded, setTemplatesLoaded] = React.useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState(null);
  const [showTemplateSelection, setShowTemplateSelection] = React.useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = React.useState(false);
  const [duplicateReview, setDuplicateReview] = React.useState(null);
  const navigation = useNavigation();
  const dispatch = useDispatch<AppDispatch>();

  // Fetch templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templatesData = await dispatch(fetchTemplates(false)).unwrap();
        setTemplates(templatesData);

        // Set default template if only one exists
        if (templatesData.length === 1) {
          setSelectedTemplateId(templatesData[0].id);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      } finally {
        setTemplatesLoaded(true);
      }
    };

    loadTemplates();
  }, [dispatch]);


  // Force root element to be visible on web
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const root = document.getElementById('root');
      if (root && root.style.display !== 'flex') {
        root.style.display = 'flex';
      }
    }
  }, []);

  // Load cached addresses for offline autocomplete when modal opens
  // Preload regardless of network status so they're ready if user goes offline
  useEffect(() => {
    const loadCachedAddresses = async () => {
      if (showNewReview) {
        try {
          if (offlineReviewService?.getCachedAddresses) {
            const addresses = await offlineReviewService.getCachedAddresses(50);
            setCachedAddresses(addresses);
          }
        } catch (error) {
          console.warn('[HomeScreen] Failed to load cached addresses:', error);
          setCachedAddresses([]);
        }
      }
    };

    loadCachedAddresses();
  }, [showNewReview]); // Removed isOnline dependency - load regardless of network status

  // Normalize address for comparison
  const normalizeAddress = React.useCallback((addr: string) => {
    return addr.toLowerCase().trim()
      .replace(/\s+/g, ' ')
      .replace(/[.,#]/g, '');
  }, []);

  const getFallbackPropertyId = React.useCallback((addressValue: string) => {
    return Math.abs(addressValue.split('').reduce((acc, char) => {
      return ((acc << 5) - acc) + char.charCodeAt(0);
    }, 0)) % 1000000;
  }, []);

  // Handle address changes from autocomplete
  // Now also accepts propertyId for offline suggestions
  const handleAddressChange = React.useCallback((newAddress, details = null, propertyId = null) => {
    setAddress(newAddress);
    setAddressDetails(details);

    if (propertyId) {
      // User explicitly selected a cached address
      setSelectedPropertyId(propertyId);
    } else if (selectedPropertyId) {
      // User is editing a previously selected cached address
      // Check if normalized address still matches - preserve propertyId for minor edits
      const cachedItem = cachedAddresses.find(c => c.propertyId === selectedPropertyId);

      if (cachedItem) {
        const normalizedNew = normalizeAddress(newAddress);
        const normalizedCached = normalizeAddress(cachedItem.address);

        if (normalizedNew === normalizedCached) {
          // Minor edit (e.g., added/removed period) - keep propertyId
        } else {
          // Significant change - clear propertyId
          setSelectedPropertyId(null);
        }
      }
    } else {
      // No previous selection - keep null
      setSelectedPropertyId(null);
    }
  }, [selectedPropertyId, cachedAddresses, normalizeAddress]);


  // Handle creating a new review
  const handleCreateReview = async () => {
    // Guard against double-tap/concurrent calls
    if (isCreating) {
      return;
    }

    // Capture the address value immediately to avoid closure issues
    const addressToSave = address.trim();

    if (!addressToSave) {
      alert('Please enter an address');
      return;
    }

    if (!memberName.trim()) {
      alert('Please enter the member name');
      return;
    }

    if (!selectedTemplateId) {
      alert('Please select a review template');
      return;
    }

    // Set guards IMMEDIATELY before any async work
    setIsCreating(true);
    setLoading(true);

    try {
      // First, check if there's already a review with this address and template.
      // Offline creation should not be blocked if this best-effort lookup cannot
      // reach the API.
      let existingReviews: any[] = [];
      try {
        existingReviews = await dispatch(fetchReviews(undefined)).unwrap();
      } catch (lookupError) {
        if (isOnline) {
          throw lookupError;
        }

        console.warn('[HomeScreen] Skipping duplicate review check while offline:', lookupError);
      }

      const requestedPropertyId = selectedPropertyId || getFallbackPropertyId(addressToSave);
      
      // Check for existing review with same address and template type
      const existingReview = existingReviews.find(review => {
        const reviewAddress = review.property?.address || review.address || '';
        const reviewPropertyId = review.propertyId || review.property?.id;
        const isSameAddress = normalizeAddress(reviewAddress) === normalizeAddress(addressToSave);
        const isSameProperty = Number(reviewPropertyId) === Number(requestedPropertyId);
        const reviewTemplateId =
          review.templateId ||
          review.template?.id ||
          (review as any).reviewData?.template?.id;
        const isSameTemplate = Number(reviewTemplateId) === Number(selectedTemplateId);
        return (isSameAddress || isSameProperty) && isSameTemplate;
      });
      
      if (existingReview) {
        // Show duplicate review dialog
        setDuplicateReview(existingReview);
        setShowDuplicateDialog(true);
        setLoading(false);
        setIsCreating(false);
        return;
      }

      // No duplicate found, proceed with creation
      await createNewReview(addressToSave, selectedTemplateId);
    } catch (error) {
      console.error('[HomeScreen] Failed to check/create review:', error);
      alert('Failed to create review: ' + error.message);
      setLoading(false);
      setIsCreating(false);
    } finally {
      // CRITICAL: Always clear loading and creating states
      setLoading(false);
      setIsCreating(false);

      // Safety net: If modal still open after 1s and not creating, force close
      setTimeout(() => {
        if (showNewReview && !isCreating) {
          setShowNewReview(false);
        }
      }, 1000);
    }
  };
  
  // Handle duplicate review options
  const handleEditExisting = () => {
    // Navigate to the existing review
    setShowDuplicateDialog(false);
    setShowNewReview(false);
    setShowTemplateSelection(false);
    setAddress('');
    setMemberName('');
    setMemberEmail('');
    setHomeManagerName('');
    setHomeManagerEmail('');
    setAddressDetails(null);
    setSelectedPropertyId(null);
    setCachedAddresses([]);

    if (duplicateReview) {
      // Navigate to the ReviewDetailScreen for the existing review
      (navigation as any).navigate('Reviews', {
        screen: 'ReviewDetail',
        params: { reviewId: duplicateReview.id }
      });
    }
  };
  
  const handleDeleteAndCreate = async () => {
    if (!duplicateReview) return;
    
    setLoading(true);
    try {
      // Delete the existing review
      await reviewService.deleteReview(duplicateReview.id);
      
      // Create new review
      setShowDuplicateDialog(false);
      await createNewReview(address.trim(), selectedTemplateId);
    } catch (error) {
      console.error('Failed to delete and create:', error);
      alert('Failed to delete existing review: ' + error.message);
      setLoading(false);
    }
  };
  
  const handleCreateDuplicate = async () => {
    // Allow creating another review with same address
    setShowDuplicateDialog(false);
    await createNewReview(address.trim(), selectedTemplateId);
  };
  
  // Separate function for actually creating the review
  const createNewReview = async (addressToSave: string, templateId: number) => {
    try {
                    // Use selectedPropertyId if user picked a cached address.
      // Otherwise look up existing property ID by normalized address, then fall back to hash
      let propertyId = selectedPropertyId;

      if (!propertyId) {
        // Try to find existing property ID for this address (handles variations like "St" vs "St.")
        if (offlineReviewService?.getPropertyIdForAddress) {
          propertyId = await offlineReviewService.getPropertyIdForAddress(addressToSave);
        }

        if (!propertyId) {
          // No match found - generate new property ID from address hash
          propertyId = getFallbackPropertyId(addressToSave);
        }
      }

      // Use Redux thunk to create review - this adds it to the store immediately
      const newReview = await dispatch(createReview({
        propertyId,
        templateId,
        address: addressToSave,
        memberName: memberName.trim(),
        memberEmail: memberEmail.trim() || null,
        homeManagerName: homeManagerName.trim() || null,
        homeManagerEmail: homeManagerEmail.trim() || null,
      })).unwrap();

      // Verify review was created successfully before navigating
      if (!newReview || !newReview.id) {
        throw new Error('Review was not created successfully');
      }

      // Close modal and reset state
      setShowNewReview(false);
      setShowTemplateSelection(false);
      setAddress('');
      setMemberName('');
      setMemberEmail('');
      setHomeManagerName('');
      setHomeManagerEmail('');
      setAddressDetails(null);
      setSelectedPropertyId(null);
      setCachedAddresses([]);

      // Navigate to the ReviewDetailScreen
      (navigation as any).navigate('Reviews', {
        screen: 'ReviewDetail',
        params: { reviewId: newReview.id }
      });
    } catch (error) {
      console.error('[HomeScreen] Failed to create review:', error);
      alert('Failed to create review: ' + (error?.message || 'Unknown error'));
    }
  };




  return (
    <View testID="home-screen" style={{
      flex: 1,
      backgroundColor: '#f5f5f5',
    }}>
      <View style={styles.screen}>
        {/* App Title */}
        <Text style={styles.appTitle}>Exhale Home Review</Text>

        {templatesLoaded && templates.length > 0 && (
          <Text testID="home-templates-ready" style={styles.detoxStateMarker}>
            {templates.length}
          </Text>
        )}
        
        {/* Start New Review Button */}
        <TouchableOpacity testID="start-new-review-button" style={styles.startButton} onPress={() => {
          // Clear any previous state
          setAddress('');
          setMemberName('');
          setMemberEmail('');
          setHomeManagerName('');
          setHomeManagerEmail('');
          setAddressDetails(null);
          setSelectedPropertyId(null);
          setCachedAddresses([]);
          setSelectedTemplateId(templates.length === 1 ? templates[0].id : null);
          setShowNewReview(true);
        }}>
          <Text style={styles.startButtonText}>+ Start New Review</Text>
        </TouchableOpacity>
        
        
        {/* New Review Modal */}
        {showNewReview && (
          <View testID="new-review-modal" style={styles.modal}>
            <KeyboardAvoidingView
              behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
              style={styles.keyboardAvoidingView}
            >
              <ScrollView
                testID="new-review-scroll"
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
              >
                <View style={styles.modalContent}>
              {!showTemplateSelection ? (
                <>
                  <Text style={styles.modalTitle}>New Review</Text>
                  <View style={styles.addressInputContainer}>
                    <AddressAutocomplete
                      testID="new-review-address-input"
                      value={address}
                      onAddressChange={handleAddressChange}
                      placeholder="Enter property address"
                      style={styles.addressInput}
                      offlineSuggestions={cachedAddresses}
                      isOnline={isOnline}
                      useOnlineLookup={useOnlineAddressLookup}
                    />
                  </View>
                  <TextInput
                    testID="new-review-member-name-input"
                    style={styles.input}
                    value={memberName}
                    onChangeText={setMemberName}
                    placeholder="Member name *"
                    autoCapitalize="words"
                  />
                  <TextInput
                    testID="new-review-member-email-input"
                    style={styles.input}
                    value={memberEmail}
                    onChangeText={setMemberEmail}
                    placeholder="Member email"
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                  <TextInput
                    testID="new-review-home-manager-name-input"
                    style={styles.input}
                    value={homeManagerName}
                    onChangeText={setHomeManagerName}
                    placeholder="Home manager name"
                    autoCapitalize="words"
                  />
                  <TextInput
                    testID="new-review-home-manager-email-input"
                    style={styles.input}
                    value={homeManagerEmail}
                    onChangeText={setHomeManagerEmail}
                    placeholder="Home manager email"
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    testID="new-review-next-button"
                    style={styles.modalButton}
                    onPress={() => {
                      if (templates.length === 1) {
                        handleCreateReview();
                      } else {
                        setShowTemplateSelection(true);
                      }
                    }}
                    disabled={loading || isCreating}
                  >
                    <Text style={styles.modalButtonText}>
                      {loading || isCreating ? 'Creating...' : 'Next'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    testID="new-review-cancel-button"
                    style={[styles.modalButton, styles.cancelButton]} 
                    onPress={() => {
                      setShowNewReview(false);
                      setAddress('');
                      setMemberName('');
                      setMemberEmail('');
                      setHomeManagerName('');
                      setHomeManagerEmail('');
                      setAddressDetails(null);
                    }}
                  >
                    <Text style={styles.modalButtonText}>Cancel</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <Text style={styles.modalTitle}>Select Review Template</Text>
                  {templates.map((template) => (
                    <TouchableOpacity
                      testID={`review-template-option-${template.id}`}
                      key={template.id}
                      style={[
                        styles.templateOption,
                        selectedTemplateId === template.id && styles.templateOptionSelected
                      ]}
                      onPress={() => setSelectedTemplateId(template.id)}
                    >
                      <Text style={styles.templateName}>{template.name}</Text>
                      <Text style={styles.templateDescription}>{template.description}</Text>
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity
                    testID="create-review-button"
                    style={[styles.modalButton, (!selectedTemplateId || isCreating) && styles.modalButtonDisabled]}
                    onPress={handleCreateReview}
                    disabled={loading || isCreating || !selectedTemplateId}
                  >
                    <Text style={styles.modalButtonText}>
                      {loading || isCreating ? 'Creating...' : 'Create Review'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    testID="review-template-back-button"
                    style={[styles.modalButton, styles.cancelButton]} 
                    onPress={() => {
                      setShowTemplateSelection(false);
                      setSelectedTemplateId(null);
                    }}
                  >
                    <Text style={styles.modalButtonText}>Back</Text>
                  </TouchableOpacity>
                  <TouchableOpacity 
                    testID="review-template-cancel-button"
                    style={[styles.modalButton, styles.cancelButton]} 
                    onPress={() => {
                      setShowNewReview(false);
                      setShowTemplateSelection(false);
                      setSelectedTemplateId(null);
                      setAddress('');
                      setMemberName('');
                      setMemberEmail('');
                      setHomeManagerName('');
                      setHomeManagerEmail('');
                      setAddressDetails(null);
                    }}
                  >
                    <Text style={styles.modalButtonText}>Cancel</Text>
                  </TouchableOpacity>
                </>
              )}
                </View>
              </ScrollView>
            </KeyboardAvoidingView>
          </View>
        )}
        
        {/* Duplicate Review Dialog */}
        {showDuplicateDialog && duplicateReview && (
          <View testID="duplicate-review-modal" style={styles.modal}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>Review Already Exists</Text>
              <Text style={styles.modalDescription}>
                A review for "{address}" with the same template already exists.
                What would you like to do?
              </Text>
              
              <TouchableOpacity 
                testID="duplicate-review-edit-existing-button"
                style={[styles.modalButton, styles.primaryButton]} 
                onPress={handleEditExisting}
              >
                <Text style={styles.modalButtonText}>Edit Existing Review</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                testID="duplicate-review-delete-create-button"
                style={[styles.modalButton, styles.warningButton]} 
                onPress={handleDeleteAndCreate}
                disabled={loading}
              >
                <Text style={styles.modalButtonText}>
                  {loading ? 'Deleting...' : 'Delete Existing & Create New'}
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                testID="duplicate-review-create-another-button"
                style={styles.modalButton} 
                onPress={handleCreateDuplicate}
                disabled={loading}
              >
                <Text style={styles.modalButtonText}>
                  {loading ? 'Creating...' : 'Create Another Review'}
                </Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                testID="duplicate-review-cancel-button"
                style={[styles.modalButton, styles.cancelButton]} 
                onPress={() => {
                  setShowDuplicateDialog(false);
                  setDuplicateReview(null);
                  setLoading(false);
                }}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    maxWidth: 800,
    width: '100%',
    alignSelf: 'center',
    backgroundColor: '#f5f5f5',
  },
  appTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 40,
  },
  detoxStateMarker: {
    height: 1,
    opacity: 0.01,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 40,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    marginBottom: 30,
  },
  startButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 8,
    marginTop: 20,
  },
  startButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: '600',
  },
  recentSection: {
    marginTop: 60,
    alignItems: 'center',
  },
  recentTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 10,
  },
  recentSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  modal: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  keyboardAvoidingView: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalContent: {
    backgroundColor: 'white',
    padding: 30,
    borderRadius: 10,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 20,
  },
  addressInputContainer: {
    width: '100%',
    marginBottom: 20,
    zIndex: 10, // Reduced zIndex
  },
  addressInput: {
    width: '100%',
    minHeight: 48,
  },
  modalButton: {
    backgroundColor: '#007AFF',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 10,
  },
  modalButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  modalButtonDisabled: {
    backgroundColor: '#ccc',
  },
  cancelButton: {
    backgroundColor: '#999',
  },
  primaryButton: {
    backgroundColor: '#00abc8',
  },
  warningButton: {
    backgroundColor: '#ff6b6b',
  },
  modalDescription: {
    fontSize: 16,
    color: '#666',
    marginBottom: 20,
    textAlign: 'center',
  },
  templateOption: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
  },
  templateOptionSelected: {
    borderColor: '#007AFF',
    backgroundColor: '#f0f8ff',
  },
  templateName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 5,
  },
  templateDescription: {
    fontSize: 14,
    color: '#666',
  },
  categoriesContainer: {
    flex: 1,
    width: '100%',
    marginBottom: 20,
  },
  categoryCard: {
    backgroundColor: '#f0f8ff',
    padding: 20,
    borderRadius: 12,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  categoryTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 5,
  },
  categoryDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 10,
  },
  categoryItems: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  tapHint: {
    fontSize: 12,
    color: '#999',
    marginTop: 5,
  },
  reviewActionButtonsContainer: {
    width: '100%',
    gap: 10,
  },
  actionButton: {
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 10,
  },
  completeReviewButton: {
    backgroundColor: '#4caf50',
  },
  actionButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  progressIndicator: {
    backgroundColor: '#f5f5f5',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 10,
  },
  progressText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  progressSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 5,
  },
  emptyText: {
    fontSize: 16,
    color: '#999',
    textAlign: 'center',
    marginTop: 20,
  },
  readOnlyBanner: {
    backgroundColor: '#e3f2fd',
    padding: 12,
    borderRadius: 8,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#90caf9',
  },
  readOnlyText: {
    fontSize: 14,
    color: '#1976d2',
    textAlign: 'center',
  },
  readOnlySummary: {
    backgroundColor: '#f5f5f5',
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 10,
  },
  readOnlySummaryText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  readOnlySummarySubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 5,
  },
  searchContainer: {
    width: '100%',
    marginBottom: 20,
  },
  searchButton: {
    width: '100%',
  },
  itemsContainer: {
    flex: 1,
    width: '100%',
    marginBottom: 20,
  },
  itemCard: {
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  itemName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 5,
  },
  itemDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  itemStatus: {
    fontSize: 12,
    color: '#999',
  },
  formContainer: {
    flex: 1,
    width: '100%',
    marginBottom: 20,
  },
  roomDropdownContainer: {
    width: '100%',
    marginBottom: 20,
  },
  formField: {
    marginBottom: 20,
  },
  fieldLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  textAreaInput: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  radioGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
    paddingVertical: 5,
  },
  radioCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#007AFF',
    marginRight: 8,
  },
  radioSelected: {
    backgroundColor: '#007AFF',
  },
  selectContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  selectOption: {
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
  },
  selectOptionSelected: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  selectOptionText: {
    fontSize: 14,
    color: '#333',
  },
  selectOptionTextSelected: {
    color: 'white',
  },
  itemSeparator: {
    marginTop: 20,
    marginBottom: 10,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  itemSeparatorText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#007AFF',
  },
  actionButtonsContainer: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: 10,
    width: '100%',
    marginTop: 20,
  },
  actionButtonHorizontal: {
    flex: Platform.OS === 'web' ? 1 : undefined,
  },
  completeButton: {
    backgroundColor: '#4caf50',
  },
  reportContainer: {
    flex: 1,
    width: '100%',
    padding: 20,
  },
  reportHeader: {
    backgroundColor: '#f5f5f5',
    padding: 20,
    borderRadius: 8,
    marginBottom: 20,
    alignItems: 'center',
  },
  reportTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  reportDate: {
    fontSize: 16,
    color: '#666',
    marginBottom: 5,
  },
  reportProperty: {
    fontSize: 18,
    fontWeight: '600',
  },
  reportSection: {
    marginBottom: 30,
  },
  reportSectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#333',
  },
  reportSectionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 15,
  },
  reportItem: {
    marginBottom: 15,
    paddingLeft: 20,
  },
  reportItemName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 3,
  },
  reportItemDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  reportResponses: {
    marginTop: 5,
    paddingLeft: 20,
  },
  reportResponseItem: {
    fontSize: 14,
    color: '#333',
    marginBottom: 2,
  },
  reportNotCompleted: {
    fontSize: 14,
    color: '#999',
    fontStyle: 'italic',
  },
  reportFooter: {
    marginTop: 40,
    padding: 20,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    alignItems: 'center',
  },
  reportFooterText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  offlineModeIndicator: {
    fontSize: 13,
    color: '#FF9500',
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: 15,
    fontStyle: 'italic',
  },
});


export default HomeScreen;
